import React, { useState } from "react";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

export default function CarRentalsEarningChart() {
    const [period, setPeriod] = useState("Month");

    const dataSets = {
        Day: Array.from({ length: 7 }, (_, i) => ({
            name: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][i],
            earning: Math.floor(Math.random() * 100000 + 50000),
        })),
        Week: Array.from({ length: 4 }, (_, i) => ({
            name: `Week ${i + 1}`,
            earning: Math.floor(Math.random() * 400000 + 100000),
        })),
        Month: [
            { name: "Jan", earning: 250000 },
            { name: "Feb", earning: 320000 },
            { name: "Mar", earning: 280000 },
            { name: "Apr", earning: 350000 },
            { name: "May", earning: 300000 },
            { name: "Jun", earning: 180000 },
            { name: "Jul", earning: 520000 },
            { name: "Aug", earning: 600000 },
            { name: "Sep", earning: 420000 },
            { name: "Oct", earning: 480000 },
            { name: "Nov", earning: 350000 },
            { name: "Dec", earning: 300000 },
        ],
        Year: [
            { name: "2020", earning: 1200000 },
            { name: "2021", earning: 1800000 },
            { name: "2022", earning: 1500000 },
            { name: "2023", earning: 2000000 },
            { name: "2024", earning: 1700000 },
        ],
    };

    const data = dataSets[period];
    const totalEarning = 6345;

    // Custom Tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const value = payload[0].value;
            return (
                <div
                    className="bg-white border rounded-3 px-3 py-1 shadow-sm"
                    style={{ fontSize: "0.75rem", fontWeight: "500" }}
                >
                    {value >= 1000000
                        ? `${(value / 1000000).toFixed(1)}m`
                        : `${(value / 1000).toFixed(0)}k`}
                </div>
            );
        }
        return null;
    };

    // Pill styles
    const tabContainerStyle = {
        display: "flex",
        alignItems: "center",
        background: "#f1f3ff",
        padding: "4px",
        borderRadius: "8px",
        gap: "4px",
        fontSize: "14px",
    };

    const pillStyle = (active) => ({
        padding: "6px 14px",
        borderRadius: "6px",
        cursor: "pointer",
        background: active ? "#4F46E5" : "transparent",
        color: active ? "#fff" : "#6B7280",
        border: "none",
        fontWeight: 500,
        transition: "all 0.2s ease",
        fontSize: "14px",
    });

    return (
        <div className="w-100 p-4 bg-white">
            <div
                className="w-100 rounded-2 border p-4"
                style={{
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    background: "#fff",
                }}
            >
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h6 className="text-muted mb-1" style={{ fontSize: "1rem" }}>
                            Total Earning
                        </h6>
                        <h4 className="fw-bold" style={{ fontSize: "2rem", margin: 0 }}>
                            {totalEarning.toLocaleString()}
                        </h4>
                    </div>

                    {/* Period Selector */}
                    <div style={tabContainerStyle}>
                        {["Day", "Week", "Month", "Year"].map((label) => (
                            <button
                                key={label}
                                style={pillStyle(period === label)}
                                onClick={() => setPeriod(label)}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Chart */}
                <ResponsiveContainer width="100%" height={320}>
                    <AreaChart
                        data={data}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                        <defs>
                            <linearGradient id="colorEarning" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#6366F1" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                            </linearGradient>
                        </defs>

                        <CartesianGrid
                            strokeDasharray="4 4"
                            vertical={false}
                            stroke="#E5E7EB"
                        />
                        <XAxis
                            dataKey="name"
                            tick={{ fontSize: 12, fill: "#6B7280" }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            tickFormatter={(val) =>
                                val >= 1000000
                                    ? `${(val / 1000000).toFixed(0)}m`
                                    : `${(val / 1000).toFixed(0)}k`
                            }
                            tick={{ fontSize: 12, fill: "#6B7280" }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ stroke: "#6366F1", strokeDasharray: "5 5" }}
                        />
                        <Area
                            type="monotone"
                            dataKey="earning"
                            stroke="#4F46E5"
                            strokeWidth={3}
                            fillOpacity={1}
                            fill="url(#colorEarning)"
                            dot={false}
                            activeDot={{
                                r: 6,
                                fill: "#4F46E5",
                                stroke: "#fff",
                                strokeWidth: 2,
                            }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}